import { ILoopFunc } from './interfaces';
import LinkedList from './LinkedList';

export class Queue<T> {
	/**
	 * List containing the elements.
	 * @type collections.LinkedList
	 * @private
	 */
	private list: LinkedList<T>;

	/**
	 * Creates an empty queue.
	 * @constructor
	 */
	/**
	 * Creates an empty instance of Queue.
	 *
	 * A queue is a first-in-first-out (FIFO) data structure, the first
	 * element added to the queue will be the first one to be removed. This
	 * implementation uses a linked list as a container.
	 *
	 * @memberof Queue
	 */
	constructor() {
		this.list = new LinkedList<T>();
	}

	/**
	 * Inserts the specified element into the end of this queue.
	 * @param {Object} elem the element to insert.
	 * @return {boolean} true if the element was inserted, or false if it is undefined.
	 */
	public enqueue(elem: T): boolean {
		return this.list.add(elem);
	}

	/**
	 * Inserts the specified element into the end of this queue.
	 * @param {T} elem the element to insert.
	 * @return {boolean} true if the element was inserted, or false if it is undefined.
	 */
	public add(elem: T): boolean {
		return this.list.add(elem);
	}

	/**
	 * Retrieves and removes the head of this queue.
	 *
	 * @returns {T} the head of this queue, or undefined if this queue is empty.
	 *
	 * @memberof Queue
	 */
	public dequeue(): T | undefined {
		return this.list.removeElementAtIndex(0);
	}

	/**
	 * Adds an item to the front of the queue.
	 *
	 * @param {T} elem the element to insert
	 * @returns {boolean} true if the element was inserted
	 *
	 * @memberof Queue
	 */
	public pushfront(elem: T): boolean {
		return this.list.add(elem, 0);
	}

	/**
	 * Retrieves, but does not remove, the head of this queue.
	 *
	 * @returns {T} the head of this queue, or undefined if this queue is empty
	 *
	 * @memberof Queue
	 */
	public peek(): T | undefined {
		return this.list.first;
	}

	/**
	 * Returns the number of elements in this queue.
	 *
	 * @returns {number} the number of elements in this queue
	 *
	 * @memberof Queue
	 */
	public size(): number {
		return this.list.size();
	}

	/**
	 * Returns the number of elements in this queue.
	 *
	 * @returns {number} the number of elements in this queue
	 *
	 * @memberof Queue
	 */
	public count(): number {
		return this.list.size();
	}

	/**
	 * Checks if this queue is empty.
	 * @return {boolean} true if and only if this queue contains no items; false
	 * otherwise.
	 */
	public isEmpty(): boolean {
		return this.list.isEmpty();
	}

	/**
	 * Removes all of the elements from this queue.
	 */
	public clear(): void {
		this.list.clear();
	}

	/**
	 * Executes the provided function once for each element present in this queue in
	 * FIFO order.
	 *
	 * @param {ILoopFunc<T>} callback function to execute, invoked with an argument
	 * of the element value. The callback can break the iteration by returning false.
	 *
	 * @memberof Queue
	 */
	public forEach(callback: ILoopFunc<T>): void {
		this.list.forEach(callback);
	}
}

export default Queue;
